/**
 * AI Apply lane selection for the job detail page (plan A3: "AI Apply on every
 * job, never hidden, honest lane label").
 *
 * Every job gets the AI Apply button. The lane only decides the LABEL, so the
 * seeker knows before the click what AI will do and what stays with them. The
 * click itself always opens Hunt on the job, whose Approve step already routes
 * each job shape (`classifyApproveApply` in ./approve-channel.ts, the headless
 * runner, the apply pack). This module never changes that routing.
 *
 * Lanes, best first:
 *  - `headless`  — Lever / Ashby: the server fills the employer's form; the
 *    seeker reviews before submit.
 *  - `prefill`   — Greenhouse: AI prepares the answers for review, the seeker
 *    submits on the employer's own form (copy buttons, or the extension). The
 *    Greenhouse Job Board API only accepts an application POST signed with
 *    the EMPLOYER's API key, so a third party can never submit there; calling
 *    this lane `headless` promised a send we cannot make.
 *  - `extension` — another ATS the browser extension autofills.
 *  - `email`     — apply-by-email posting: AI drafts the application email.
 *  - `pack`      — everything else (careers page, aggregator, hosted form): AI
 *    drafts answers + cover letter for the seeker to paste.
 *
 * Pure and import-free so the server page and the client button share it.
 */

/**
 * ATS buckets (from `detectAutofillSupportedAts`) where AI fills the form
 * server-side. Also the classes the 2026-10 apply benchmark measured end to end
 * (scripts/research/apply-benchmark-2026-10/README.md). Do not widen without a
 * benchmark run for that ATS.
 */
export const AI_APPLY_PRIMARY_ATS: ReadonlySet<string> = new Set([
	"greenhouse",
	"lever",
	"ashby",
]);

export function isAiApplyPrimaryAts(ats: string | null | undefined): boolean {
	return ats != null && AI_APPLY_PRIMARY_ATS.has(ats);
}

export type AiApplyLane =
	| "headless"
	| "prefill"
	| "extension"
	| "email"
	| "pack";

/** ATS buckets where AI prepares answers but the seeker submits (see `prefill`). */
const PREFILL_ATS: ReadonlySet<string> = new Set(["greenhouse"]);

export interface AiApplyLaneInput {
	/** `detectAutofillSupportedAts` bucket, or null (no autofill-supported ATS). */
	ats: string | null;
	/** `usesEmailApplyChannel` result for this job. */
	isEmailApply: boolean;
}

export function selectAiApplyLane(input: AiApplyLaneInput): AiApplyLane {
	if (input.isEmailApply) return "email";
	if (input.ats !== null && PREFILL_ATS.has(input.ats)) return "prefill";
	if (isAiApplyPrimaryAts(input.ats)) return "headless";
	if (input.ats !== null) return "extension";
	return "pack";
}

/** Copy shown on the button. Strings come from `common.json` (id + en). */
export interface AiApplyLaneCopy {
	label: string;
	hint: string;
	/** Prepended to `hint` for a guest whose click goes through sign-in. */
	guestPrefix: string;
}

const LANE_KEY: Record<AiApplyLane, string> = {
	headless: "Headless",
	prefill: "Prefill",
	extension: "Extension",
	email: "Email",
	pack: "Pack",
};

/** Pick this lane's strings from the page's `common` dictionary. */
export function aiApplyLaneCopy(
	t: Record<string, string>,
	lane: AiApplyLane,
): AiApplyLaneCopy {
	const k = LANE_KEY[lane];
	return {
		label: t[`aiApplyLane${k}Label`] ?? "",
		hint: t[`aiApplyLane${k}Hint`] ?? "",
		guestPrefix: t.aiApplyLaneGuestPrefix ?? "",
	};
}
